import { useEffect, useMemo, useState } from 'react';
import type { DeliveryAppeal } from '../../../../lib/api';

/**
 * Selection state for the customer bulk (multi-select) appeal-response feature
 * on the Submitted Appeals sub-tab.
 *
 * An appeal is eligible for a bulk accept/reject when it is a supplier-raised
 * appeal still awaiting the customer's response — exactly the rows that show
 * the single "Respond" button. When the feature is disabled (supplier viewer
 * or the platform toggle is off) nothing is eligible and no checkboxes render.
 *
 * @param appeals - the appeals currently shown on the page
 * @param enabled - whether the bulk-respond feature is available to this viewer
 * @returns selection set + derived eligible/selected lists and mutators
 */
export function useAppealBulkRespond(appeals: DeliveryAppeal[], enabled: boolean) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<'accept' | 'reject' | null>(null);

  const eligibleAppeals = useMemo(
    () => (enabled ? appeals.filter((a) => a.status === 'awaiting_response' && !a.is_customer_appeal) : []),
    [appeals, enabled],
  );
  const eligibleIds = useMemo(() => new Set(eligibleAppeals.map((a) => a.id)), [eligibleAppeals]);

  // Drop any selection that is no longer on the page (pagination / refetch).
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => eligibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [eligibleIds]);

  const isEligible = (a: DeliveryAppeal) => eligibleIds.has(a.id);
  const selectedAppeals = appeals.filter((a) => selectedIds.has(a.id));
  const allEligibleSelected = eligibleAppeals.length > 0 && eligibleAppeals.every((a) => selectedIds.has(a.id));

  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds((prev) => {
      if (eligibleAppeals.every((a) => prev.has(a.id))) return new Set();
      return new Set(eligibleAppeals.map((a) => a.id));
    });

  const clear = () => setSelectedIds(new Set());

  return {
    enabled,
    selectedIds,
    selectedAppeals,
    eligibleAppeals,
    allEligibleSelected,
    isEligible,
    toggle,
    toggleAll,
    clear,
    bulkAction,
    setBulkAction,
  };
}

/** Everything the companion bar / checkbox cells need from the hook. */
export type AppealBulkRespond = ReturnType<typeof useAppealBulkRespond>;
